import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { ApiError, apiGet, apiPost } from 'utils/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [status, setStatus] = useState('loading');
  const [session, setSession] = useState(null);

  const refreshSession = async () => {
    try {
      const nextSession = await apiGet('/auth/session');
      setSession(nextSession);
      setStatus('authenticated');
      return nextSession;
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        setSession(null);
        setStatus('guest');
        return null;
      }

      setSession(null);
      setStatus('guest');
      throw error;
    }
  };

  useEffect(() => {
    refreshSession().catch(() => {});
  }, []);

  const login = async (credentials) => {
    const nextSession = await apiPost('/auth/login', credentials);
    setSession(nextSession);
    setStatus('authenticated');
    return nextSession;
  };

  const register = async (payload) => {
    return apiPost('/auth/register', payload);
  };

  const requestMagicLink = async (payload) => apiPost('/auth/magic-link/request', payload);

  const consumeMagicLink = async (payload) => {
    const nextSession = await apiPost('/auth/magic-link/consume', payload);
    setSession(nextSession);
    setStatus('authenticated');
    return nextSession;
  };

  const requestPasswordReset = async (payload) => apiPost('/auth/password-reset/request', payload);

  const consumePasswordReset = async (payload) => {
    const nextSession = await apiPost('/auth/password-reset/consume', payload);
    setSession(nextSession);
    setStatus('authenticated');
    return nextSession;
  };

  const logout = async () => {
    await apiPost('/auth/logout');
    setSession(null);
    setStatus('guest');
  };

  const value = useMemo(
    () => ({
      status,
      session,
      user: session?.user || null,
      workspace: session?.workspace || null,
      isAuthenticated: status === 'authenticated',
      isGuest: status === 'guest',
      isLoading: status === 'loading',
      login,
      register,
      requestMagicLink,
      consumeMagicLink,
      requestPasswordReset,
      consumePasswordReset,
      logout,
      refreshSession
    }),
    [session, status]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider');
  }

  return context;
}
